import { useEffect, useRef, useState } from "react";
import type { Exercise } from "../core/exercises";
import {
  buildRecording, bundleChunks, bundleName, PLACEMENTS, sampleRate, type Placement, type RawRecording, type StoredRecording,
} from "../core/motion";
import { loadRepSensor, profileOf, saveRepSensor, withLearned } from "../core/repSensor";
import { newId, RECORDINGS, type Backend } from "../core/storage";
import { platform } from "../platform";
import { APP_VERSION } from "../version";
import { useBack } from "./back";
import { ConfirmDialog } from "./ConfirmDialog";
import { playCues, unlockAudio } from "./cues";
import { DurationPicker } from "./DurationPicker";
import { HoldButton } from "./HoldButton";
import { liveCount, type LiveCount } from "./liveCount";
import type { MotionSource } from "./motionSource";
import { formatDay, formatDuration, t } from "./texts";
import { useWakeLock } from "./useWorkout";

const LEAD_INS = [3, 10, 20];
const TICK_MS = 100;
/** remembered per phone (a convenience: without storage the defaults apply) */
const SETTINGS_KEY = "workout-helper.record";

interface Settings {
  /** "count": the Zähltest (todo 8), the sensor counts live */
  mode: "record" | "count";
  exerciseId?: string;
  placement: Placement;
  durationSec: number;
  leadInSec: number;
}

function loadSettings(): Settings {
  const defaults: Settings = { mode: "record", placement: "pocket", durationSec: 40, leadInSec: 10 };
  try {
    return { ...defaults, ...JSON.parse(localStorage.getItem(SETTINGS_KEY) ?? "{}") };
  } catch {
    return defaults;
  }
}

function storeSettings(s: Settings) {
  try {
    localStorage.setItem(SETTINGS_KEY, JSON.stringify(s));
  } catch {
    // private mode or blocked storage: the settings are not remembered
  }
}

type Phase =
  | { kind: "setup" }
  | { kind: "running"; startedAt: number; recordedAt: number }
  | { kind: "review"; raw: RawRecording; recordedAt: number; counted?: number };

type Confirm = "cancel" | "discard" | "deleteAll" | { delete: StoredRecording };

/**
 * Hidden screen for todo 7 (five taps on the version in Verlauf): records the motion sensors during one timed set; the
 * owner does not count during the set (touches through a trouser pocket would count too, owner 2026-10-30) but enters
 * the reps afterwards. Keeps the recording on the phone (`RECORDINGS`) and exports all
 * of them at once as one file for `tests/fixtures/motion/`.
 *
 * The Zähltest (todo 8) is the same with the rep detection running: it counts live (from the profile learned for the
 * exercise and placement), the review shows the count next to the real reps, and saving keeps the recording with
 * both and lets the profile learn from it. `onLearned`: the profiles changed.
 */
export function RecordScreen(props: { db: Backend; exercises: Exercise[]; onClose(): void; onLearned?(): void }) {
  const { db } = props;
  const exercises = props.exercises.filter((e) => !e.hidden)
    .sort((a, b) => Number(b.measure === "timed") - Number(a.measure === "timed") || a.name.localeCompare(b.name, "de"));
  const [settings, setSettings] = useState(loadSettings);
  const exercise = exercises.find((e) => e.id === settings.exerciseId) ?? exercises[0];
  const [phase, setPhase] = useState<Phase>({ kind: "setup" });
  const [now, setNow] = useState(Date.now);
  /** the reps done, entered after the set; undefined until then (or while the field does not parse) */
  const [reps, setReps] = useState<number>();
  const [repsText, setRepsText] = useState("");
  const [notes, setNotes] = useState("");
  const [rate, setRate] = useState<number>();
  const [picking, setPicking] = useState(false);
  const [confirm, setConfirm] = useState<Confirm>();
  const [error, setError] = useState<string>();
  const [notice, setNotice] = useState<string>();
  /** the recordings on the phone, newest first; undefined while loading */
  const [stored, setStored] = useState<StoredRecording[]>();
  const source = useRef<MotionSource>(undefined);
  /** the Zähltest's counter and its live count */
  const counter = useRef<LiveCount>(undefined);
  const [live, setLive] = useState(0);
  const counting = settings.mode === "count";
  const flags = useRef({ started: false, beeped: -1, finishing: false });
  useWakeLock(phase.kind === "running");

  const loadStored = () => db.getAll<StoredRecording>(RECORDINGS)
    .then((all) => setStored(all.sort((a, b) => b.recordedAt - a.recordedAt)))
    .catch((e) => setError(t.record.loadFailed(e instanceof Error ? e.message : String(e))));
  useEffect(() => void loadStored(), [db]);

  const change = (s: Partial<Settings>) => setSettings((old) => { const next = { ...old, ...s }; storeSettings(next); return next; });
  const running = phase.kind === "running" ? phase : undefined;
  // the browser's data is not the APK's
  const web = platform.name === "web";
  const leadMs = settings.leadInSec * 1000;
  const setMs = settings.durationSec * 1000;

  const start = async () => {
    unlockAudio();
    setError(undefined);
    setNotice(undefined);
    let src: MotionSource | undefined;
    try {
      // from the tap: Safari asks for the permission here
      src = await platform.motion();
      if (!src) return setError(t.record.noSensors);
      const profile = counting && exercise ? profileOf(await loadRepSensor(db), exercise.id, settings.placement) : undefined;
      await src.start();
      counter.current?.dispose();
      counter.current = counting ? liveCount(src, Date.now(), profile, setLive) : undefined;
    } catch (e) {
      return setError(t.record.startFailed(e instanceof Error ? e.message : String(e)));
    }
    setLive(0);
    source.current = src;
    flags.current = { started: false, beeped: -1, finishing: false };
    setRate(undefined);
    setNotes("");
    setNow(Date.now());
    setPhase({ kind: "running", startedAt: Date.now(), recordedAt: Date.now() });
  };

  const finish = async (recordedAt: number) => {
    const src = source.current;
    if (!src || flags.current.finishing) return;
    flags.current.finishing = true;
    if (flags.current.started) src.mark("setEnd");
    playCues([{ at: Date.now(), beep: "end" }]);
    try {
      const raw = await src.stop();
      source.current = undefined;
      const counted = counter.current?.finish(raw);
      counter.current = undefined;
      changeReps(undefined);
      setPhase({ kind: "review", raw, recordedAt, counted });
    } catch (e) {
      source.current = undefined;
      setError(t.record.stopFailed(e instanceof Error ? e.message : String(e)));
      setPhase({ kind: "setup" });
    }
  };

  const cancel = () => {
    void source.current?.stop();
    source.current = undefined;
    counter.current?.dispose();
    counter.current = undefined;
    setConfirm(undefined);
    setPhase({ kind: "setup" });
  };

  // the clock: lead-in, then the set; beeps for the last 3 s of each, "go" at the start, the end stops the recording
  useEffect(() => {
    if (!running) return;
    const id = setInterval(() => {
      const n = Date.now();
      setNow(n);
      const src = source.current;
      if (!src) return;
      const elapsed = n - running.startedAt;
      const f = flags.current;
      if (!f.started && elapsed >= leadMs) {
        f.started = true;
        src.mark("setStart");
        counter.current?.begin(Date.now());
        playCues([{ at: n, beep: "go" }]);
      }
      const left = f.started ? leadMs + setMs - elapsed : leadMs - elapsed;
      const sec = Math.ceil(left / 1000);
      if (sec >= 1 && sec <= 3 && f.beeped !== sec + (f.started ? 10 : 0)) {
        f.beeped = sec + (f.started ? 10 : 0);
        playCues([{ at: n, beep: "count" }]);
      }
      if (f.started && elapsed >= leadMs + setMs) void finish(running.recordedAt);
    }, TICK_MS);
    return () => clearInterval(id);
  }, [running, leadMs, setMs]);

  // the measured sample rate, once a second
  useEffect(() => {
    if (!running || !source.current) return;
    const src = source.current;
    let last: number | undefined;
    const id = setInterval(() => {
      void src.status().then((s) => {
        if (last !== undefined) setRate(s.accel - last);
        last = s.accel;
      }).catch(() => undefined);
    }, 1000);
    return () => clearInterval(id);
  }, [running]);

  // leaving the screen stops the sensors
  useEffect(() => () => { counter.current?.dispose(); void source.current?.stop(); }, []);

  useBack(() => {
    if (phase.kind === "running") setConfirm("cancel");
    else if (phase.kind === "review") setConfirm("discard");
    else props.onClose();
  });

  const exportAll = async () => {
    if (!stored?.length) return;
    setError(undefined);
    setNotice(undefined);
    const now = Date.now();
    const name = bundleName(now, stored.length);
    try {
      // the stored records carry their id, which the file does not need
      const recordings = stored.map(({ id: _, ...r }) => r);
      if (await platform.saveFile(name, bundleChunks(recordings, now), "application/json")) setNotice(t.record.exported(name, stored.length));
      else setError(t.data.notSaved);
    } catch (e) {
      setError(t.data.saveFailed(e instanceof Error ? e.message : String(e)));
    }
  };

  const remove = async (which: StoredRecording[]) => {
    setConfirm(undefined);
    setNotice(undefined);
    try {
      for (const r of which) await db.delete(RECORDINGS, r.id);
    } catch (e) {
      setError(t.record.deleteFailed(e instanceof Error ? e.message : String(e)));
    }
    await loadStored();
  };

  const changeReps = (n: number | undefined) => { setReps(n); setRepsText(n === undefined ? "" : String(n)); };

  const dialogs = confirm && (
    confirm === "cancel" ? (
      <ConfirmDialog danger text={t.record.confirmCancel} confirm={t.record.cancelYes}
        onCancel={() => setConfirm(undefined)} onConfirm={cancel} />
    ) : confirm === "discard" ? (
      <ConfirmDialog danger text={t.record.confirmDiscard} confirm={t.record.discard}
        onCancel={() => setConfirm(undefined)} onConfirm={() => { setConfirm(undefined); setPhase({ kind: "setup" }); }} />
    ) : confirm === "deleteAll" ? (
      <ConfirmDialog danger text={t.record.confirmDeleteAll(stored?.length ?? 0)} confirm={t.record.deleteAll}
        onCancel={() => setConfirm(undefined)} onConfirm={() => void remove(stored ?? [])} />
    ) : (
      <ConfirmDialog danger text={t.record.confirmDelete(describe(confirm.delete), formatDay(confirm.delete.recordedAt))} confirm={t.record.delete}
        onCancel={() => setConfirm(undefined)} onConfirm={() => void remove([confirm.delete])} />
    )
  );

  if (running && exercise) {
    const elapsed = now - running.startedAt;
    const ready = elapsed < leadMs;
    const left = ready ? leadMs - elapsed : Math.max(0, leadMs + setMs - elapsed);
    const phaseClass = ready ? "phase-ready" : left <= 3000 ? "phase-ending" : "phase-work";
    return (
      <div className={"timer-screen " + phaseClass} role="dialog">
        <header className="timer-head">
          <button className="timer-ghost" onClick={() => setConfirm("cancel")}>{t.timer.cancel}</button>
        </header>
        <div className="timer-title">{t.record.runTitle(exercise.name, t.record.placement[settings.placement])}</div>
        <div className="timer-label">{ready ? t.record.stow : t.timer.work}</div>
        <div className="timer-big">{ready ? String(Math.ceil(left / 1000)) : formatDuration(Math.ceil(left / 1000))}</div>
        <div className="timer-goal">{rate === undefined ? t.record.measuring : t.record.rate(rate)}</div>
        {counter.current ? (
          <div className="sensor-count" role="status">
            <span className="tap-count">{live}</span>
            <span className="tap-hint">{counter.current.live ? t.record.liveCount(live) : t.sensor.countAtEnd}</span>
          </div>
        ) : <div className="tap-spacer record-hint">{t.record.countInHead}</div>}
        <div className="timer-actions">
          <HoldButton className="timer-btn strong" label={t.record.holdToStop} onHeld={() => void finish(running.recordedAt)} />
        </div>
        {dialogs}
      </div>
    );
  }

  if (phase.kind === "review" && exercise) {
    const { raw } = phase;
    const save = async () => {
      const r = buildRecording(raw, {
        recordedAt: phase.recordedAt, appVersion: APP_VERSION, placement: settings.placement, durationSec: settings.durationSec, reps: reps!, notes,
        exercise: { id: exercise.id, name: exercise.name, equipment: exercise.equipment, measure: exercise.measure },
        counted: phase.counted,
      });
      try {
        await db.put(RECORDINGS, { id: newId(), ...r });
        // the Zähltest teaches the exercise's profile, as a sensor set in a workout does
        if (phase.counted !== undefined) {
          await saveRepSensor(db, withLearned(await loadRepSensor(db), r, r.reps));
          props.onLearned?.();
        }
      } catch (e) {
        return setError(t.record.storeFailed(e instanceof Error ? e.message : String(e)));
      }
      setError(undefined);
      const stored = t.record.stored(describe(r));
      setNotice(phase.counted === undefined ? stored : t.record.comparison(phase.counted, r.reps) + " " + stored);
      setPhase({ kind: "setup" });
      await loadStored();
    };
    return (
      <>
        <header className="titlebar">
          <button className="ghost" onClick={() => setConfirm("discard")}>{t.back}</button>
        </header>
        <h1>{t.record.reviewTitle}</h1>
        <p>{exercise.name} · {t.record.placement[settings.placement]} · {formatDuration(settings.durationSec)}</p>
        <p className="muted-text">{t.record.samples(raw.accel.t.length, sampleRate(raw.accel.t), raw.gyro ? sampleRate(raw.gyro.t) : undefined)}</p>
        {raw.accel.t.length === 0 && <p className="error">{t.record.noSamples}</p>}
        {phase.counted !== undefined && <p className="big-note">{t.record.counted(phase.counted)}</p>}

        <div className="label">{t.record.reps}</div>
        <div className="stepper">
          <button className="secondary" disabled={!reps} onClick={() => changeReps(reps! - 1)}>−1</button>
          <input className="value" aria-label={t.record.reps} inputMode="numeric" value={repsText} placeholder="?"
            onChange={(e) => {
              setRepsText(e.target.value);
              const v = e.target.value.trim();
              setReps(/^\d{1,3}$/.test(v) ? Number(v) : undefined);
            }} />
          <button className="secondary" onClick={() => changeReps((reps ?? 0) + 1)}>+1</button>
        </div>
        <p className="muted-text">{t.record.repsHint}</p>
        {phase.counted !== undefined && reps !== undefined && <p className="big-note">{t.record.comparison(phase.counted, reps)}</p>}

        <label className="label">{t.record.notes}
          <textarea className="field" rows={2} value={notes} placeholder={t.record.notesPlaceholder} onChange={(e) => setNotes(e.target.value)} />
        </label>

        {error && <p className="error">{error}</p>}
        <div className="action-stack">
          <button className="primary" disabled={reps === undefined} onClick={save}>{t.record.save}</button>
          <button className="secondary" onClick={() => setConfirm("discard")}>{t.record.discard}</button>
        </div>
        {dialogs}
      </>
    );
  }

  return (
    <>
      <header className="titlebar">
        <button className="ghost" onClick={props.onClose}>{t.back}</button>
      </header>
      <h1>{counting ? t.record.titleCount : t.record.title}</h1>
      <div className="segmented">
        {(["record", "count"] as const).map((m) => (
          <button key={m} className={settings.mode === m ? "active" : ""} onClick={() => change({ mode: m })}>
            {m === "record" ? t.record.modeRecord : t.record.modeCount}
          </button>
        ))}
      </div>
      <p className="muted-text">{counting ? t.record.introCount : web ? t.record.introWeb : t.record.intro}</p>
      {web && <div className="notice"><p>{t.record.webData}</p></div>}
      {exercises.length === 0 && <p className="error">{t.record.noExercises}</p>}

      <label className="label">{t.record.exercise}
        <select className="field" value={exercise?.id ?? ""} onChange={(e) => change({ exerciseId: e.target.value })}>
          {exercises.map((e) => <option key={e.id} value={e.id}>{e.name} · {t.measure(e.measure)}</option>)}
        </select>
      </label>

      <div className="label">{t.record.where}</div>
      <div className="segmented">
        {PLACEMENTS.map((p) => (
          <button key={p} className={settings.placement === p ? "active" : ""} onClick={() => change({ placement: p })}>{t.record.placementShort[p]}</button>
        ))}
      </div>

      <div className="label">{t.record.duration}</div>
      <button className="secondary wide-field" onClick={() => setPicking(true)}>{formatDuration(settings.durationSec)}</button>

      <div className="label">{t.record.leadIn}</div>
      <div className="segmented">
        {LEAD_INS.map((s) => (
          <button key={s} className={settings.leadInSec === s ? "active" : ""} onClick={() => change({ leadInSec: s })}>{s} s</button>
        ))}
      </div>
      <p className="muted-text">{t.record.leadInHint}</p>

      {notice && <div className="notice"><p>{notice}</p></div>}
      {error && <p className="error">{error}</p>}
      <button className="primary wide" disabled={!exercise} onClick={start}>{counting ? t.record.startCount : t.record.start}</button>

      <h2>{t.record.storedTitle(stored?.length ?? 0)}</h2>
      {stored && stored.length === 0 && <p className="muted-text">{t.record.noneStored}</p>}
      {stored && stored.length > 0 && (
        <>
          <ul className="list">
            {stored.map((r) => (
              <li key={r.id} className="recording-row">
                <div>
                  <span className="name">{describe(r)}</span>
                  <span className="sub">{formatDay(r.recordedAt)} · {formatDuration(r.durationSec)} · {Math.round(r.rateHz.accel)} Hz</span>
                </div>
                <button className="ghost icon" aria-label={t.record.delete} onClick={() => setConfirm({ delete: r })}>✕</button>
              </li>
            ))}
          </ul>
          <p className="muted-text">{t.record.exportHint}</p>
          <div className="action-stack">
            <button className="primary" onClick={exportAll}>{t.record.exportAll(stored.length)}</button>
            <button className="secondary" onClick={() => setConfirm("deleteAll")}>{t.record.deleteAll}</button>
          </div>
        </>
      )}

      {picking && (
        <DurationPicker title={t.record.duration} value={settings.durationSec} fallback={40}
          onCancel={() => setPicking(false)}
          onPick={(sec) => { setPicking(false); if (sec) change({ durationSec: sec }); }} />
      )}
      {dialogs}
    </>
  );
}

/** One line per recording: exercise, placement, reps. */
const describe = (r: Pick<StoredRecording, "exercise" | "placement" | "reps">) =>
  t.record.describe(r.exercise.name, t.record.placementShort[r.placement], r.reps);

